import ApiError from '@/lib/api/ApiError';
import { useContext, useEffect, useState } from 'react';
import UploadedImagesContext from '../../UploadedImagesContext';
import ShouldGetImagesFromPublicApiContext from '../../ShouldGetImagesFromPublicApiContext';
import api from '@/lib/api';
import HttpMethod from '@/lib/constants/HttpMethod.enum';
import ApiHandlingContext from '@/lib/api/ApiHandlingContext';

const useLoadImage = ({
  imageId,
  useThumbnail,
}: {
  imageId: number;
  useThumbnail?: boolean;
}): [true, null, null] | [false, Blob, null] | [false, null, unknown] => {
  const { imagesEndpoint } = useContext(UploadedImagesContext);
  const shouldGetImagesFromPublicApi = useContext(
    ShouldGetImagesFromPublicApiContext,
  );

  const imageEndpoint = `${imagesEndpoint}/${imageId}${useThumbnail ? '/thumbnail' : ''}${shouldGetImagesFromPublicApi ? '/public' : ''}`;

  const storedResponse = api.getStoredGlobalStaticResponse<Blob>(imageEndpoint);

  const [loading, setLoading] = useState(storedResponse === null);
  const [response, setResponse] = useState<Blob | null>(storedResponse);
  const [error, setError] = useState<unknown | null>(null);

  const handleApiError = useContext(ApiHandlingContext);

  const fetchApiResponse = async (): Promise<void> => {
    setLoading(true);

    try {
      const apiResponse = await api.getGlobalStatic<Blob>(imageEndpoint);

      setResponse(apiResponse);
    } catch (error) {
      if (!(error instanceof ApiError)) {
        handleApiError(error, HttpMethod.GET);
      }

      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchApiResponse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageEndpoint]);

  if (loading || (response === null && error === null)) {
    return [true, null, null];
  }

  if (!loading && response === null) {
    return [false, null, error];
  }

  return [false, response, null];
};

export default useLoadImage;
